import React, {useEffect, useState, useMemo} from 'react'
import {Line} from 'react-chartjs-2'
import 'chart.js/auto'
import {API_URL} from '../../../modules/auth/core/_requests'

type BrandChartData = {
  date: string
  store: string
  brand: string
  quantity: number
  revenue: number
}

const ecBrands = [
  'ARMANI EXCHANGE',
  'ARNETTE',
  'COSTA DEL MAR',
  'OAKLEY',
  'PERSOL',
  'RAY-BAN',
  'VOGUE',
  'BVLGARI',
  'GUESS',
  'POLO RALPH LAUREN',
  'RALPH LAUREN',
  'RALPH BY RALPH LAUREN',
  'SWAROSKI',
  'TIFFANY & CO',
  'TORY BURCH',
  'MICHAEL KORS',
]

const vintageBrands = [
  'CALVIN KLEIN',
  'DOLCE & GABBANA',
  'HARLEY-DAVIDSON',
  'SMITH',
  'UNDER ARMOUR',
  'VERSACE',
  'BURBERRY',
  'FILA',
  'KENNETH COLE',
]

const dateOptions = [
  'last7Days',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'last30Days',
  'last90Days',
  'last6Months',
  'thisYear',
  'lastYear',
  'last12Months',
]

const dateLabels: {[key: string]: string} = {
  last7Days: 'Last 7 Days',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  last30Days: 'Last 30 Days',
  last90Days: 'Last 90 Days',
  last6Months: 'Last 6 Months',
  thisYear: 'This Year',
  lastYear: 'Last Year',
  last12Months: 'Last 12 Months',
}

const intervalOptions = ['day', 'week', 'month']

function parseIsoDate(str: string): Date {
  const parts = str.split('-').map(Number)
  if (parts.length === 3) {
    return new Date(parts[0], parts[1] - 1, parts[2])
  }
  return new Date(str)
}

function addInterval(base: Date, interval: string): Date {
  const d = new Date(base.getTime())
  if (interval === 'day') {
    d.setDate(d.getDate() + 1)
  } else if (interval === 'week') {
    d.setDate(d.getDate() + 7)
  } else {
    d.setMonth(d.getMonth() + 1)
  }
  return d
}

function formatDateYMD(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = ('0' + (d.getMonth() + 1)).slice(-2)
  const dd = ('0' + d.getDate()).slice(-2)
  return `${yyyy}-${mm}-${dd}`
}

function fillMissingDates(data: BrandChartData[], interval: string): BrandChartData[] {
  const byBrand: {[key: string]: BrandChartData[]} = {}
  for (const row of data) {
    if (!byBrand[row.brand]) {
      byBrand[row.brand] = []
    }
    byBrand[row.brand].push(row)
  }
  const result: BrandChartData[] = []
  for (const brand of Object.keys(byBrand)) {
    const arr = byBrand[brand].sort((a, b) => (a.date < b.date ? -1 : 1))
    if (!arr.length) continue
    const minDate = parseIsoDate(arr[0].date)
    const maxDate = parseIsoDate(arr[arr.length - 1].date)
    const map = new Map<string, BrandChartData>()
    for (const r of arr) {
      map.set(r.date, r)
    }
    let cursor = new Date(minDate.getTime())
    while (cursor.getTime() <= maxDate.getTime()) {
      const key = formatDateYMD(cursor)
      if (map.has(key)) {
        result.push(map.get(key)!)
      } else {
        result.push({
          date: key,
          store: arr[0].store,
          brand,
          quantity: 0,
          revenue: 0,
        })
      }
      cursor = addInterval(cursor, interval)
    }
  }
  return result.sort((a, b) => (a.date < b.date ? -1 : 1))
}

async function fetchDataRange(date: string, interval: string): Promise<BrandChartData[]> {
  const url = `${API_URL}/amazon-brand-line-graph/chart-data?store=both&date=${encodeURIComponent(
    date
  )}&interval=${encodeURIComponent(interval)}`
  const resp = await fetch(url)
  const j = await resp.json()
  return j.data || []
}

async function fetchAll(interval: string): Promise<BrandChartData[]> {
  const url = `${API_URL}/amazon-brand-line-graph/chart-data?store=both&interval=${encodeURIComponent(
    interval
  )}`
  const resp = await fetch(url)
  const j = await resp.json()
  return j.data || []
}

function filterByDateRange(
  data: BrandChartData[],
  interval: string,
  rangeKey: string
): BrandChartData[] {
  if (!data.length) return []
  const now = new Date()
  let endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let startDate = new Date(endDate.getTime())
  if (rangeKey === 'last7Days') {
    startDate.setDate(startDate.getDate() - 6)
  } else if (rangeKey === 'thisWeek') {
    const dayOfWeek = startDate.getDay()
    startDate.setDate(startDate.getDate() - dayOfWeek)
  } else if (rangeKey === 'lastWeek') {
    const dayOfWeek = startDate.getDay()
    startDate.setDate(startDate.getDate() - dayOfWeek - 7)
    endDate = new Date(startDate.getTime())
    endDate.setDate(endDate.getDate() + 6)
  } else if (rangeKey === 'thisMonth') {
    startDate.setDate(1)
  } else if (rangeKey === 'lastMonth') {
    startDate.setMonth(startDate.getMonth() - 1, 1)
    endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    endDate.setMonth(endDate.getMonth() + 1, 0)
  } else if (rangeKey === 'last30Days') {
    startDate.setDate(startDate.getDate() - 29)
  } else if (rangeKey === 'last90Days') {
    startDate.setDate(startDate.getDate() - 89)
  } else if (rangeKey === 'last6Months') {
    startDate.setMonth(startDate.getMonth() - 5, 1)
  } else if (rangeKey === 'thisYear') {
    startDate = new Date(startDate.getFullYear(), 0, 1)
  } else if (rangeKey === 'lastYear') {
    startDate.setFullYear(startDate.getFullYear() - 1, 0, 1)
    endDate = new Date(startDate.getFullYear(), 11, 31)
  } else if (rangeKey === 'last12Months') {
    startDate.setFullYear(startDate.getFullYear(), startDate.getMonth() - 11, 1)
  }
  const minISO = formatDateYMD(startDate)
  const maxISO = formatDateYMD(endDate)
  const filtered = data.filter((d) => d.date >= minISO && d.date <= maxISO)
  const filled = fillMissingDates(filtered, interval)
  return filled
}

const cacheAll: {
  dayAll?: BrandChartData[]
  weekAll?: BrandChartData[]
  monthAll?: BrandChartData[]
} = {}

export const AmazonBrandSalesLineChart: React.FC = () => {
  const [loadingInitial, setLoadingInitial] = useState(true)
  const [loadingPrefetch, setLoadingPrefetch] = useState(false)
  const [initialData, setInitialData] = useState<BrandChartData[]>([])
  const [activeTab, setActiveTab] = useState<'units' | 'revenue'>('revenue')
  const [selectedDate, setSelectedDate] = useState('last30Days')
  const [selectedInterval, setSelectedInterval] = useState('day')
  const [logScale, setLogScale] = useState(false)
  const [finalData, setFinalData] = useState<BrandChartData[]>([])

  const allBrands = [...ecBrands, ...vintageBrands]

  // Brand visibility on/off
  const [brandFilter, setBrandFilter] = useState<Record<string, boolean>>(
    allBrands.reduce((acc, b) => {
      acc[b] = true
      return acc
    }, {} as Record<string, boolean>)
  )

  // Color palette
  const colorPalette = [
    '#36A2EB',
    '#FF6384',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#00A67C',
    '#FFA500',
    '#008080',
    '#8A2BE2',
    '#FF1493',
    '#7FFFD4',
    '#CD5C5C',
    '#1E90FF',
    '#2F4F4F',
    '#FFD700',
    '#ADFF2F',
    '#DC143C',
    '#00CED1',
    '#F4A460',
    '#708090',
    '#BA55D3',
    '#556B2F',
    '#20B2AA',
    '#FF4500',
    '#E9967A',
    '#8B008B',
  ]

  const brandToColor: {[key: string]: string} = {}
  let colorIndex = 0
  for (const b of allBrands) {
    brandToColor[b] = colorPalette[colorIndex % colorPalette.length]
    colorIndex++
  }

  useEffect(() => {
    let cancel = false
    async function loadDefault() {
      const d = await fetchDataRange('last30Days', 'day')
      if (!cancel) {
        setInitialData(d)
        setLoadingInitial(false)
      }
    }
    loadDefault()
    return () => {
      cancel = true
    }
  }, [])

  useEffect(() => {
    if (!loadingInitial && initialData.length) {
      setFinalData(fillMissingDates(initialData, 'day'))
      setLoadingPrefetch(true)
      Promise.all([fetchAll('day'), fetchAll('week'), fetchAll('month')]).then(
        ([dayArr, weekArr, monthArr]) => {
          cacheAll.dayAll = dayArr
          cacheAll.weekAll = weekArr
          cacheAll.monthAll = monthArr
          setLoadingPrefetch(false)
        }
      )
    }
  }, [loadingInitial, initialData])

  useEffect(() => {
    if (selectedInterval === 'day') {
      if (cacheAll.dayAll) {
        const filtered = filterByDateRange(cacheAll.dayAll, 'day', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'day').then((d) => {
          setFinalData(fillMissingDates(d, 'day'))
        })
      }
    } else if (selectedInterval === 'week') {
      if (cacheAll.weekAll) {
        const filtered = filterByDateRange(cacheAll.weekAll, 'week', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'week').then((d) => {
          setFinalData(fillMissingDates(d, 'week'))
        })
      }
    } else {
      if (cacheAll.monthAll) {
        const filtered = filterByDateRange(cacheAll.monthAll, 'month', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'month').then((d) => {
          setFinalData(fillMissingDates(d, 'month'))
        })
      }
    }
  }, [selectedDate, selectedInterval])

  // Summed totals for each brand (over the current finalData)
  const brandTotals = useMemo(() => {
    const totals: Record<string, {quantity: number; revenue: number}> = {}
    for (const row of finalData) {
      if (!totals[row.brand]) {
        totals[row.brand] = {quantity: 0, revenue: 0}
      }
      totals[row.brand].quantity += row.quantity
      totals[row.brand].revenue += row.revenue
    }
    return totals
  }, [finalData])

  // For the legend: sort brands by top-most revenue or top-most units
  function sortBrands(brands: string[]): string[] {
    const sorted = [...brands].sort((a, b) => {
      const aVal =
        activeTab === 'units' ? brandTotals[a]?.quantity || 0 : brandTotals[a]?.revenue || 0
      const bVal =
        activeTab === 'units' ? brandTotals[b]?.quantity || 0 : brandTotals[b]?.revenue || 0
      return bVal - aVal // Desc order
    })
    return sorted
  }

  // Chart data
  const groupedData = useMemo(() => {
    const map: {[key: string]: {date: string; quantity: number; revenue: number}[]} = {}
    for (const row of finalData) {
      if (!map[row.brand]) {
        map[row.brand] = []
      }
      map[row.brand].push({date: row.date, quantity: row.quantity, revenue: row.revenue})
    }
    return map
  }, [finalData])

  const chartData = useMemo(() => {
    const labels = Array.from(new Set(finalData.map((item) => item.date))).sort()
    const datasets =
      activeTab === 'units'
        ? Object.keys(groupedData)
            .filter((brand) => brandFilter[brand] !== false)
            .map((brand) => {
              return {
                label: brand,
                data: labels.map((lbl) => {
                  const found = groupedData[brand].find((x) => x.date === lbl)
                  return found ? found.quantity : 0
                }),
                borderColor: brandToColor[brand] || '#FFFFFF',
                backgroundColor: (brandToColor[brand] || '#FFFFFF') + '33',
                fill: false,
              }
            })
        : Object.keys(groupedData)
            .filter((brand) => brandFilter[brand] !== false)
            .map((brand) => {
              return {
                label: brand,
                data: labels.map((lbl) => {
                  const found = groupedData[brand].find((x) => x.date === lbl)
                  return found ? found.revenue : 0
                }),
                borderColor: brandToColor[brand] || '#FFFFFF',
                backgroundColor: (brandToColor[brand] || '#FFFFFF') + '33',
                fill: false,
              }
            })
    return {labels, datasets}
  }, [finalData, activeTab, groupedData, brandToColor, brandFilter])

  // Chart options
  const options = useMemo(() => {
    return {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed.y
              if (activeTab === 'revenue') {
                return `${ctx.dataset.label}: $${val}`
              }
              return `${ctx.dataset.label}: ${val}`
            },
          },
        },
      },
      scales: {
        x: {
          type: 'category' as const,
          ticks: {
            
          },
          grid: {
            color: 'rgba(255,255,255,0.1)',
          },
        },
        y: {
          type: logScale ? ('logarithmic' as const) : ('linear' as const),
          ticks: {
            callback: (value: any) => {
              if (activeTab === 'revenue') {
                return '$' + value
              }
              return value
            },
          },
          grid: {
            color: 'rgba(255,255,255,0.1)',
          },
        },
      },
      responsive: true as const,
      maintainAspectRatio: false,
    }
  }, [logScale, activeTab])

  // Toggle brand filter
  const toggleBrand = (b: string) => {
    setBrandFilter((old) => ({
      ...old,
      [b]: !old[b],
    }))
  }

  // Floating “Inspector” state
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null)
  const [mousePos, setMousePos] = useState<{x: number; y: number}>({x: 0, y: 0})

  // Legend with iOS-style toggles + sorting + hover for popup
  const Legend = () => {
    const RenderStoreSection = (title: string, rawBrands: string[]) => {
      // Sort them
      const brands = sortBrands(rawBrands)

      return (
        <div style={{marginBottom: '1rem'}}>
          <div style={{fontWeight: 'bold', marginBottom: '0.3rem', fontSize: '13px'}}>{title}</div>
          {brands.map((b) => (
            <div
              key={b}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginTop: '3px',
              }}
              onMouseEnter={(e) => {
                setHoveredBrand(b)
              }}
              onMouseMove={(e) => {
                // track mouse so popup can follow
                setMousePos({x: e.clientX, y: e.clientY})
              }}
              onMouseLeave={() => {
                setHoveredBrand(null)
              }}
              onClick={() => toggleBrand(b)}
            >
              {/* iOS-style toggle track */}
              <div
                style={{
                  position: 'relative',
                  width: '27px',
                  height: '13px',
                  borderRadius: '8px',
                  backgroundColor: brandFilter[b] ? brandToColor[b] : '#aaa',
                  marginRight: '5px',
                  transition: 'background-color 0.2s ease',
                  flexShrink: 0,
                }}
              >
                {/* The toggle knob */}
                <div
                  style={{
                    position: 'absolute',
                    top: '1px',
                    left: brandFilter[b] ? 'calc(100% - 14px)' : '1px',
                    width: '14px',
                    height: '11px',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    transition: 'left 0.2s ease',
                  }}
                />
              </div>
              <span style={{fontSize: '13px'}}>{b}</span>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div style={{ overflowY: 'auto'}}>
        {RenderStoreSection('EC AMZ', ecBrands)}
        {RenderStoreSection('VINTAGE', vintageBrands)}
      </div>
    )
  }

  // Render floating inspector if hoveredBrand is set
  const InspectorPopup = () => {
    if (!hoveredBrand) return null

    // find brand totals
    const totals = brandTotals[hoveredBrand] || {quantity: 0, revenue: 0}
    const labelUnits = `Units: ${totals.quantity}`
    const labelRevenue = `Revenue: $${totals.revenue.toLocaleString()}`

    // We'll do a "frosted glass" style: translucent background, slight blur
    const style: React.CSSProperties = {
      position: 'fixed',
      top: mousePos.y + 16, // a bit below the cursor
      left: mousePos.x + 16, // a bit to the right
      zIndex: 9999,
      backdropFilter: 'blur(8px)',
      padding: '8px 12px',
      borderRadius: '10px',
      pointerEvents: 'none',
      maxWidth: '200px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      fontSize: '12px',
      transition: 'top 0.05s, left 0.05s', // smooth follow
    }

    return (
      <div style={style}>
        <div style={{fontWeight: 600, marginBottom: '4px'}}>{hoveredBrand}</div>
        <div>{labelUnits}</div>
        <div>{labelRevenue}</div>
      </div>
    )
  }

  return (
    <div
      style={{
        borderRadius: '12px',
        border: 'none',
        position: 'relative',
      }}
      className='card p-4 card-body text-body bg-body'
    >
      <h2 style={{ fontWeight: 500}} className='mb-3'>
        Sales Data
      </h2>
      {loadingInitial && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            fontSize: '1.2rem',
            fontWeight: 500,
            borderRadius: '8px',
          }}
        >
          Loading chart...
        </div>
      )}
      {!loadingInitial && (
        <>
          <div className='mb-3 d-flex flex-row' style={{gap: '1rem', maxWidth: '600px'}}>
            <div style={{flex: '1'}}>
              <label style={{}} className='form-label'>
                Date Range
              </label>
              <select
                className='form-select'
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{borderRadius: '8px'}}
              >
                {dateOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {dateLabels[opt]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{flex: '1'}}>
              <label style={{color: 'white'}} className='form-label'>
                Interval
              </label>
              <select
                className='form-select'
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                style={{borderRadius: '8px'}}
              >
                {intervalOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ul className='nav mt-2 pb-4 mb-4' style={{borderBottom: '1px solid #ddd'}}>
            <li className='nav-item me-3'>
              <button
                className={`btn button btn-outline ${activeTab === "units" ? "btn-primary" : ""}`}
                style={{
                  padding: '0.5rem 0.8rem',
                }}
                onClick={() => setActiveTab('units')}
              >
                Units
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`btn button btn-outline ${activeTab === "revenue" ? "btn-primary" : ""}`}
                style={{
                  padding: '0.5rem 0.8rem',
                }}
                onClick={() => setActiveTab('revenue')}
              >
                Revenue
              </button>
            </li>
          </ul>
          <div
            className='d-flex align-items-center mt-2 mb-4'
            style={{gap: '1rem', flexWrap: 'wrap'}}
          >
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={logScale}
                onChange={(e) => setLogScale(e.target.checked)}
              />
              <label className='form-check-label'>Logarithmic Scale</label>
            </div>
            <div className='form-check form-switch'>{loadingPrefetch && <span> Preloading Data...</span>} </div>
          </div>
          <div className='mb-3 mt-2' style={{display: 'flex'}}>
            <Legend />
            <div style={{height: '630px', flex: 1, position: 'relative'}}>
              <Line data={chartData} options={options} />
            </div>
          </div>
          {/* Floating Inspector Popup */}
          <InspectorPopup />
        </>
      )}
    </div>
  )
}
